import { useEffect, useMemo, useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Gift,
  Coins,
  Receipt,
  TrendingUp,
  Wallet,
  CheckCircle2,
  Clock,
  XCircle,
  Sparkles,
  Users,
  SlidersHorizontal,
  X,
  Copy,
  Check,
} from "lucide-react";
import { api } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";

/* -------------------------------------------------------------------------- */
/*  Type → label/color meta                                                    */
/* -------------------------------------------------------------------------- */
const TX_META = {
  deposit:        { label: "Deposit",         icon: ArrowDownToLine, dir: "in",  color: "#21c46e" },
  agent_deposit:  { label: "Agent Deposit",   icon: ArrowDownToLine, dir: "in",  color: "#21c46e" },
  withdrawal:     { label: "Withdrawal",      icon: ArrowUpFromLine, dir: "out", color: "#f59e0b" },
  agent_withdraw: { label: "Agent Withdraw",  icon: ArrowUpFromLine, dir: "out", color: "#f59e0b" },
  bet:            { label: "Bet",             icon: Coins,           dir: "out", color: "#a855f7" },
  win:            { label: "Win",             icon: TrendingUp,      dir: "in",  color: "#21c46e" },
  bonus:          { label: "Deposit Bonus",   icon: Gift,            dir: "in",  color: "#f5c451" },
  agent_bonus:    { label: "Agent Bonus",     icon: Gift,            dir: "in",  color: "#f5c451" },
  referral_bonus: { label: "Referral",        icon: Users,           dir: "in",  color: "#3b82f6" },
  vip_cashback:   { label: "VIP Cashback",    icon: Sparkles,        dir: "in",  color: "#ec4899" },
  wckt_cashout:   { label: "WICKET return",   icon: TrendingUp,      dir: "in",  color: "#21c46e" },
  adjustment:     { label: "Adjustment",      icon: Wallet,          dir: "in",  color: "#64748b" },
};
const META = (t) => {
  if (t && t.type === "deposit" && t.method === "agent_deposit") {
    return TX_META.agent_deposit;
  }
  if (t && t.type === "withdrawal" && t.method === "agent_withdraw") {
    return TX_META.agent_withdraw;
  }
  if (t && t.type === "bonus" && t.method === "agent_deposit_bonus") {
    return TX_META.agent_bonus;
  }
  return TX_META[t?.type] || TX_META.adjustment;
};

/* True if the row was created by an agent action (used for UI accents). */
function isAgentRow(t) {
  return (
    t?.method === "agent_deposit" ||
    t?.method === "agent_withdraw" ||
    t?.method === "agent_deposit_bonus" ||
    !!t?.agent_id ||
    !!t?.agent_username
  );
}

const STATUS_META = {
  completed: { label: "Approved",   color: "#21c46e", icon: CheckCircle2 },
  success:   { label: "Approved",   color: "#21c46e", icon: CheckCircle2 },
  pending:   { label: "Pending",    color: "#f5c451", icon: Clock },
  paying:    { label: "Processing", color: "#f5c451", icon: Clock },
  failed:    { label: "Failed",     color: "#ef4444", icon: XCircle },
  rejected:  { label: "Rejected",   color: "#ef4444", icon: XCircle },
};
const statusMeta = (s) => STATUS_META[s] || { label: s || "—", color: "#94a3b8", icon: Clock };

/* Pretty method/provider maps */
const EWALLET_NAMES = { bkash: "bKash", nagad: "Nagad", rocket: "Rocket", upay: "UPay", tap: "Tap", okwallet: "OK Wallet" };
const CRYPTO_NAMES  = { usdt: "USDT", btc: "BTC", eth: "ETH", trx: "TRX" };
const PROVIDER_NAMES = {
  cowpay: "CowPay", starpago: "StarPago", wpay: "WPay", hrpay: "HRpay",
  nowpayments: "NowPayments", okpay: "OKPay", paypal: "PayPal", stripe: "Stripe",
  razorpay: "Razorpay", manual: "Manual", agent: "Agent", tadopay: "TadoPay",
};

/* Brand logo mapping for the inline icon in each row */
const METHOD_LOGOS = {
  bkash: "/payment-logos/bkash.png",
  nagad: "/payment-logos/nagad.png",
  rocket: "/payment-logos/rocket.png",
  upay: "/payment-logos/upay.png",
  usdt: "/payment-logos/usdt.svg",
  btc: "/payment-logos/btc.svg",
  eth: "/payment-logos/eth.svg",
  trx: "/payment-logos/trx.svg",
};

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */
function fmtAmount(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}
function fmtDateOnly(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${date}, ${time}`;
  } catch { return iso; }
}
function methodKey(t) {
  // Best guess at the method id (lowercase canonical) for icon + display
  const m = (t.gateway_method || t.method || "").toString().toLowerCase().trim();
  // Some t.method values look like "Nagad · Nagad Payment · CowPay BD" — take first token
  return m.split(/[·\s,]/)[0] || "";
}
function methodPretty(t) {
  const id = methodKey(t);
  if (EWALLET_NAMES[id]) return EWALLET_NAMES[id].toUpperCase();
  if (CRYPTO_NAMES[id])  return CRYPTO_NAMES[id];
  const raw = String(t.method || "").split("·")[0].trim();
  return raw || "—";
}
function providerPretty(t) {
  // 1) Direct canonical provider id (e.g. "cowpay")
  const p = String(t.provider || t.gateway_provider || "").toLowerCase();
  if (PROVIDER_NAMES[p]) return PROVIDER_NAMES[p];
  if (p) return p.charAt(0).toUpperCase() + p.slice(1);
  // 2) Try to detect provider key inside gateway_label / method (e.g. "CowPay BD")
  const blob = `${t.gateway_label || ""} ${t.method || ""}`.toLowerCase();
  for (const key of Object.keys(PROVIDER_NAMES)) {
    if (blob.includes(key)) return PROVIDER_NAMES[key];
  }
  // 3) Last segment of t.method ("Nagad · Nagad Payment · CowPay BD" → "CowPay BD")
  const parts = String(t.method || "").split("·").map((s) => s.trim()).filter(Boolean);
  if (parts.length > 1) return parts[parts.length - 1];
  // 4) Raw gateway_label
  return String(t.gateway_label || "");
}
/* Short TX id helper above. For bonus rows, surface the percentage / source
 * (e.g. "7% · Site Deposit"). Best-effort guess from t.note / t.promotion_name
 * / t.source — backend writes free-text labels here. */
function bonusSubtitle(t) {
  const blob = `${t.promotion_name || ""} ${t.note || ""} ${t.source || ""}`;
  const m = blob.match(/(\d{1,3})\s*%/);
  const pct = m ? `${m[1]}%` : "";
  const src = /agent/i.test(blob) ? "Agent" : (blob ? "Site" : "");
  if (pct && src) return `${src} · ${pct}`;
  if (pct) return pct;
  if (src) return src;
  return String(t.note || t.promotion_name || "").trim();
}

/* Gateway Order Number — same value shown in admin "All Deposits" → Gateway
 * Order No column. For gateway deposit transactions this is the deposit doc id
 * (carried as `t.deposit_id` on the transactions feed); for withdrawals it's
 * `t.withdraw_id`; for agent rows the human-readable `AGT-...` / `WAG-...`
 * reference is preferred since admin sees the same ref in their lists. */
function shortTxId(t) {
  const isAgentRowFast = t.method === "agent_deposit"
    || t.method === "agent_withdraw"
    || t.method === "agent_deposit_bonus";
  if (isAgentRowFast) {
    return (
      t.agent_withdrawal_id ||  // raw uuid (linked agent_withdrawals._id)
      t.reference ||             // AGT-... / WAG-... short ref
      t.id ||
      ""
    ).toString();
  }
  return (
    t.deposit_id ||
    t.withdraw_id ||
    t.gateway_order_no ||
    t.id ||
    t.reference ||
    ""
  ).toString();
}

/* -------------------------------------------------------------------------- */
/*  Main page                                                                  */
/* -------------------------------------------------------------------------- */
export default function TransactionRecordsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("all"); // all | 7d | 15d | 30d
  const [typeFilter, setTypeFilter] = useState("all"); // all | deposit | withdrawal | bonus | bet
  const [showFilter, setShowFilter] = useState(false);
  const [selected, setSelected] = useState(null); // open detail modal for this tx

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/profile/transactions");
        if (!cancelled) setItems(data.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const cutoff = range === "7d"  ? Date.now() - 7  * 86400000
                 : range === "15d" ? Date.now() - 15 * 86400000
                 : range === "30d" ? Date.now() - 30 * 86400000
                 : 0;
    return items.filter((t) => {
      if (cutoff) {
        const ts = new Date(t.created_at).getTime();
        if (!ts || ts < cutoff) return false;
      }
      if (typeFilter !== "all") {
        if (typeFilter === "bonus") {
          if (!["bonus", "referral_bonus", "vip_cashback"].includes(t.type)) return false;
        } else if (t.type !== typeFilter) return false;
      }
      return true;
    });
  }, [items, range, typeFilter]);

  /* Group by exact date (DD/MM/YYYY) */
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const key = fmtDateOnly(t.created_at);
      if (!map.has(key)) map.set(key, { key, items: [] });
      map.get(key).items.push(t);
    }
    return Array.from(map.values());
  }, [filtered]);

  return (
    <ProfileShell title="Transaction Records">
      <style>{`
        @keyframes tx-rise { 0% { opacity: 0; transform: translateY(6px); } 100% { opacity: 1; transform: none; } }
        .tx-rise { animation: tx-rise .35s cubic-bezier(.2,.8,.2,1) both; }
      `}</style>

      <div className="pb-24 sm:pb-10" data-testid="transactions-page">

        {/* ===== Top filter row ===== */}
        <div className="flex items-center gap-2" data-testid="tx-filter-row">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none flex-1 min-w-0 -mx-1 px-1">
            {[
              { id: "all", label: "All" },
              { id: "7d",  label: "7 Days" },
              { id: "15d", label: "15 Days" },
              { id: "30d", label: "1 Month" },
            ].map((r) => {
              const active = range === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => setRange(r.id)}
                  data-testid={`tx-range-${r.id}`}
                  className={`h-9 px-3 sm:h-10 sm:px-4 rounded-lg sm:rounded-xl text-[12px] sm:text-[13px] font-bold whitespace-nowrap shrink-0 transition active:scale-[0.97] ${
                    active ? "text-white" : "text-white/65"
                  }`}
                  style={
                    active
                      ? { background: "#21c46e", boxShadow: "0 6px 18px -10px rgba(33,196,110,0.65)" }
                      : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }
                  }
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          {/* Filter button — icon only on phone, icon+label on larger */}
          <button
            onClick={() => setShowFilter(true)}
            className="h-9 sm:h-10 w-9 sm:w-auto sm:px-3.5 rounded-lg sm:rounded-xl text-[12px] sm:text-[13px] font-bold whitespace-nowrap inline-flex items-center justify-center sm:gap-1.5 text-white/80 transition active:scale-[0.97] shrink-0 relative"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
            data-testid="tx-filter-btn"
            aria-label="Filter"
          >
            <SlidersHorizontal size={15} strokeWidth={2.2} />
            <span className="hidden sm:inline">Filter</span>
            {typeFilter !== "all" && (
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-emerald-400 sm:static sm:h-1.5 sm:w-1.5" />
            )}
          </button>
        </div>

        {/* ===== Loading skeletons ===== */}
        {loading && (
          <div className="mt-5 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-[140px] rounded-2xl bg-white/[0.025] border border-white/[0.05] animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
            ))}
          </div>
        )}

        {/* ===== Empty state ===== */}
        {!loading && filtered.length === 0 && <EmptyState hasFilter={range !== "all" || typeFilter !== "all"} />}

        {/* ===== Grouped list ===== */}
        {!loading && grouped.map((group, gi) => (
          <div key={group.key} className="mt-5 tx-rise" style={{ animationDelay: `${60 + gi * 40}ms` }}>
            {/* Date header */}
            <div className="text-white/85 text-[13px] font-bold tabular-nums mb-2.5" data-testid={`tx-date-${group.key}`}>
              {group.key}
            </div>
            {/* Cards */}
            <div className="space-y-2.5">
              {group.items.map((t, i) => (
                <TxCard
                  key={t.id || t.reference || `${group.key}-${i}`}
                  t={t}
                  onOpenDetails={() => setSelected(t)}
                />
              ))}
            </div>
          </div>
        ))}

        {/* ===== Footer count ===== */}
        {!loading && filtered.length > 0 && (
          <div className="mt-8 text-center text-white/60 text-[14px] font-semibold" data-testid="tx-footer-count">
            — {filtered.length} Transaction Record{filtered.length === 1 ? "" : "s"} —
          </div>
        )}
      </div>

      {/* ===== Filter modal ===== */}
      {showFilter && (
        <FilterModal
          current={typeFilter}
          onSelect={(id) => { setTypeFilter(id); setShowFilter(false); }}
          onClose={() => setShowFilter(false)}
        />
      )}

      {/* ===== Detail modal ===== */}
      {selected && (
        <DetailModal t={selected} onClose={() => setSelected(null)} />
      )}
    </ProfileShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Transaction list card (bajilo-style)                                       */
/* -------------------------------------------------------------------------- */
function TxCard({ t, onOpenDetails }) {
  const m = META(t);
  const s = statusMeta(t.status);
  const SIcon = s.icon;
  const isIn = m.dir === "in";
  const amount = Number(t.amount || 0);
  const logo = METHOD_LOGOS[methodKey(t)];
  const methodLabel = methodPretty(t);
  const hasMethod = methodLabel && methodLabel !== "—";

  return (
    <button
      onClick={onOpenDetails}
      className="block w-full text-left rounded-2xl px-4 py-3.5 sm:px-5 sm:py-4 transition active:scale-[0.992] hover:bg-white/[0.045]"
      style={{
        background: "rgba(255,255,255,0.025)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
      data-testid="tx-card"
    >
      {/* Top row: status pill + amount */}
      <div className="flex items-center justify-between gap-3">
        <span
          className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded-md"
          style={{
            color: s.color,
            background: `${s.color}1a`,
            border: `1px solid ${s.color}33`,
          }}
          data-testid="tx-status-pill"
        >
          <SIcon size={11} strokeWidth={2.4} />
          {s.label}
        </span>
        <span
          className="text-[17px] sm:text-[19px] font-extrabold tabular-nums whitespace-nowrap shrink-0"
          style={{ color: isIn ? "#21c46e" : "#f59e0b" }}
        >
          {isIn ? "+" : "−"}৳ {fmtAmount(Math.abs(amount))}
        </span>
      </div>

      {/* Title + inline method/logo (compact) */}
      <div className="mt-2 flex items-center gap-2 min-w-0">
        <span className="text-[17px] sm:text-[18px] font-extrabold text-white leading-tight">
          {m.label}
        </span>
        {/* Agent-credited DEPOSIT or WITHDRAW: blue "Agent" tag */}
        {((t.type === "deposit" && t.method === "agent_deposit") ||
          (t.type === "withdrawal" && t.method === "agent_withdraw")) && (
          <>
            <span className="text-white/25 text-[14px]">·</span>
            <span
              className="text-[12px] font-bold px-1.5 py-0.5 rounded-md whitespace-nowrap"
              style={{ color: "#3b82f6", background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.22)" }}
              data-testid="tx-card-agent-tag"
            >
              Agent
              {t.agent_username ? ` · ${t.agent_username}` : ""}
            </span>
          </>
        )}
        {/* Agent BONUS row: gold "Agent · 5%" tag */}
        {t.type === "bonus" && t.method === "agent_deposit_bonus" && (
          <>
            <span className="text-white/25 text-[14px]">·</span>
            <span
              className="text-[12px] font-bold px-1.5 py-0.5 rounded-md whitespace-nowrap"
              style={{ color: "#f5c451", background: "rgba(245,196,81,0.10)", border: "1px solid rgba(245,196,81,0.22)" }}
            >
              Agent{bonusSubtitle(t).match(/\d+%/) ? ` · ${bonusSubtitle(t).match(/\d+%/)[0]}` : ""}
            </span>
          </>
        )}
        {/* Site BONUS row: gold "Site · 7%" tag */}
        {t.type === "bonus" && t.method !== "agent_deposit_bonus" && bonusSubtitle(t) && (
          <>
            <span className="text-white/25 text-[14px]">·</span>
            <span
              className="text-[12px] font-bold px-1.5 py-0.5 rounded-md whitespace-nowrap"
              style={{ color: "#f5c451", background: "rgba(245,196,81,0.10)", border: "1px solid rgba(245,196,81,0.22)" }}
            >
              {bonusSubtitle(t)}
            </span>
          </>
        )}
        {/* Regular gateway deposit/withdrawal: method + brand logo */}
        {((t.type === "deposit" && t.method !== "agent_deposit") ||
          (t.type === "withdrawal" && t.method !== "agent_withdraw")) && hasMethod && (
          <>
            <span className="text-white/25 text-[14px]">·</span>
            {logo ? (
              <img
                src={logo}
                alt=""
                className="h-4 w-4 rounded-full bg-white object-contain p-[1.5px] shrink-0"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : null}
            <span className="text-white/75 text-[13px] sm:text-[14px] font-semibold truncate">
              {methodLabel}
            </span>
          </>
        )}
      </div>

      {/* Bottom: time left + Details right */}
      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className="text-white/45 text-[12px] tabular-nums truncate" data-testid="tx-card-time">
          {fmtDateTime(t.created_at)}
        </span>
        <span
          className="text-emerald-400 text-[13px] font-bold inline-flex items-center gap-0.5 shrink-0"
          data-testid="tx-card-details"
        >
          Details
          <span className="text-[15px] leading-none">›</span>
        </span>
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  Detail modal (bajilo-style full screen)                                    */
/* -------------------------------------------------------------------------- */
function DetailModal({ t, onClose }) {
  const m = META(t);
  const s = statusMeta(t.status);
  const isIn = m.dir === "in";
  const amount = Number(t.amount || 0);
  const logo = METHOD_LOGOS[methodKey(t)];
  const idValue = shortTxId(t);
  const [copied, setCopied] = useState(false);

  const copyId = async () => {
    if (!idValue) return;
    try {
      await navigator.clipboard.writeText(String(idValue));
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignore */ }
  };

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Decide which Payment Method / Remark to display, taking agent rows into account
  const isAgentDep = t.type === "deposit"    && t.method === "agent_deposit";
  const isAgentWd  = t.type === "withdrawal" && t.method === "agent_withdraw";
  const isAgentBns = t.type === "bonus"      && t.method === "agent_deposit_bonus";
  const isAgentRowAny = isAgentDep || isAgentWd || isAgentBns;
  const isGatewayDep = t.type === "deposit"    && !isAgentDep;
  const isGatewayWd  = t.type === "withdrawal" && !isAgentWd;
  const showPaymentMethod = isGatewayDep || isGatewayWd;

  let remarkValue = "";
  if (isAgentRowAny) {
    remarkValue = t.agent_username
      ? `Agent · ${t.agent_username}`
      : "Agent";
  } else {
    remarkValue = providerPretty(t);
  }

  const rows = [
    { label: "ID",             value: idValue || "—", mono: true, copyable: true },
    { label: "Type",           value: m.label },
    showPaymentMethod
      ? { label: "Payment Method", value: methodPretty(t) }
      : null,
    remarkValue ? { label: "Remark", value: remarkValue } : null,
    // Agent withdraw: surface the 6-digit handover code that the user shares
    // with the agent to claim the funds in person.
    (isAgentWd && t.code)
      ? { label: "Withdraw Code", value: String(t.code).toUpperCase(), mono: true }
      : null,
    { label: "Amount",         value: `৳ ${fmtAmount(Math.abs(amount))}` },
    { label: "Date",           value: fmtDateTime(t.created_at) },
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col"
      style={{ background: "#0a0e15" }}
      data-testid="tx-detail-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="mx-auto w-full max-w-[440px] flex-1 flex flex-col px-5 pt-3 pb-5">
        {/* Top close X */}
        <div className="flex justify-end -mr-1">
          <button
            onClick={onClose}
            className="h-11 w-11 rounded-full inline-flex items-center justify-center text-white/65 hover:text-white hover:bg-white/[0.05] transition"
            data-testid="tx-detail-close-x"
            aria-label="Close"
          >
            <X size={24} strokeWidth={2} />
          </button>
        </div>

        {/* Hero — logo + amount + subtitle + status pill */}
        <div className="flex flex-col items-center text-center pt-3 pb-2">
          <div
            className="h-16 w-16 rounded-full flex items-center justify-center mb-4"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            {logo ? (
              <img src={logo} alt="" className="h-12 w-12 rounded-full object-contain bg-white p-1" />
            ) : (
              <m.icon size={28} style={{ color: m.color }} strokeWidth={2.2} />
            )}
          </div>
          <div
            className="text-[40px] sm:text-[44px] font-extrabold tabular-nums leading-none"
            style={{ color: isIn ? "#21c46e" : "#f59e0b" }}
            data-testid="tx-detail-amount"
          >
            {isIn ? "+" : "−"}৳ {fmtAmount(Math.abs(amount))}
          </div>
          <div className="mt-3 text-[16px] text-white/85 font-semibold">
            {m.label}
            {t.provider || t.gateway_id ? " Payment Gateway" : ""}
          </div>
          <span
            className="mt-4 inline-flex items-center text-[13px] font-bold px-3 py-1.5 rounded-md"
            style={{
              color: s.color,
              background: `${s.color}1a`,
              border: `1px solid ${s.color}33`,
            }}
            data-testid="tx-detail-status"
          >
            {s.label}
          </span>
        </div>

        {/* Detail rows card */}
        <div
          className="mt-6 rounded-2xl"
          style={{
            background: "rgba(255,255,255,0.025)",
            border: "1px solid rgba(255,255,255,0.05)",
          }}
          data-testid="tx-detail-rows"
        >
          <div className="px-5 py-2">
            {rows.map((r, i) => (
              <div
                key={r.label}
                className={`flex items-start justify-between gap-4 py-3 ${
                  i === rows.length - 1 ? "" : "border-b"
                }`}
                style={i === rows.length - 1 ? {} : { borderColor: "rgba(255,255,255,0.04)" }}
              >
                <span className="text-white/55 text-[15px] shrink-0">{r.label}</span>
                <div className="flex items-center gap-2 min-w-0 justify-end flex-1">
                  <span
                    className={`text-white text-[15px] font-semibold text-right truncate ${r.mono ? "font-mono" : ""}`}
                    title={String(r.value)}
                  >
                    {r.value}
                  </span>
                  {r.copyable && r.value && r.value !== "—" && (
                    <button
                      onClick={copyId}
                      className="h-7 w-7 rounded-md inline-flex items-center justify-center text-white/55 hover:text-white hover:bg-white/[0.06] transition shrink-0"
                      title={copied ? "Copied!" : "Copy ID"}
                      aria-label="Copy ID"
                      data-testid="tx-detail-copy-id"
                    >
                      {copied ? (
                        <Check size={14} className="text-emerald-400" />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Close button */}
        <div className="mt-auto pt-6">
          <button
            onClick={onClose}
            className="w-full h-[58px] rounded-xl text-white text-[17px] font-bold inline-flex items-center justify-center active:scale-[0.99] transition"
            style={{
              background: "linear-gradient(180deg, #25cf78 0%, #1ea863 100%)",
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.18) inset, 0 -1px 0 rgba(0,0,0,0.18) inset, 0 14px 34px -16px rgba(33,196,110,0.55)",
            }}
            data-testid="tx-detail-close-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Type filter modal — bottom sheet on mobile, centered on desktop            */
/* -------------------------------------------------------------------------- */
function FilterModal({ current, onSelect, onClose }) {
  const options = [
    { id: "all",        label: "All Transactions", desc: "Show everything",                icon: Receipt,         color: "#94a3b8" },
    { id: "deposit",    label: "Deposits",         desc: "Money added to wallet",          icon: ArrowDownToLine, color: "#21c46e" },
    { id: "withdrawal", label: "Withdrawals",      desc: "Cash-outs and payouts",          icon: ArrowUpFromLine, color: "#f59e0b" },
    { id: "bonus",      label: "Bonuses",          desc: "Welcome, referral, VIP rewards", icon: Gift,            color: "#f5c451" },
  ];

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="tx-filter-modal"
    >
      <div
        className="w-full sm:max-w-[400px] rounded-t-3xl sm:rounded-2xl overflow-hidden animate-[tx-sheet-up_.28s_cubic-bezier(.2,.8,.2,1)_both]"
        style={{
          background: "#0f1622",
          border: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 -24px 60px -20px rgba(0,0,0,0.5)",
        }}
      >
        <style>{`
          @keyframes tx-sheet-up { 0% { transform: translateY(20px); opacity: 0; } 100% { transform: none; opacity: 1; } }
        `}</style>

        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-2 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-white/15" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div>
            <h3 className="text-white font-bold text-[17px] leading-tight">Filter by type</h3>
            <p className="text-white/45 text-[12px] mt-0.5">Choose a transaction category</p>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full inline-flex items-center justify-center text-white/60 hover:text-white hover:bg-white/[0.06] transition shrink-0"
            data-testid="tx-filter-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Options */}
        <div className="px-3 pb-3">
          {options.map((o) => {
            const active = current === o.id;
            const OIcon = o.icon;
            return (
              <button
                key={o.id}
                onClick={() => onSelect(o.id)}
                data-testid={`tx-filter-opt-${o.id}`}
                className={`w-full rounded-xl px-3.5 py-3 flex items-center gap-3 text-left transition active:scale-[0.985] ${
                  active ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"
                }`}
                style={
                  active
                    ? { border: `1px solid ${o.color}40` }
                    : { border: "1px solid transparent" }
                }
              >
                <div
                  className="h-10 w-10 rounded-lg inline-flex items-center justify-center shrink-0"
                  style={{
                    background: `${o.color}1a`,
                    color: o.color,
                    border: `1px solid ${o.color}33`,
                  }}
                >
                  <OIcon size={18} strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-[14px] font-bold leading-tight">{o.label}</div>
                  <div className="text-white/45 text-[11.5px] mt-0.5 truncate">{o.desc}</div>
                </div>
                {active && (
                  <div
                    className="h-6 w-6 rounded-full inline-flex items-center justify-center shrink-0"
                    style={{ background: o.color }}
                  >
                    <Check size={14} className="text-black" strokeWidth={3} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer safe area for iOS */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Empty                                                                      */
/* -------------------------------------------------------------------------- */
function EmptyState({ hasFilter }) {
  return (
    <div className="mt-10 rounded-2xl py-14 px-5 text-center tx-rise"
      style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.05)" }}
      data-testid="tx-empty"
    >
      <div className="h-16 w-16 rounded-2xl bg-emerald-500/[0.07] mx-auto flex items-center justify-center mb-3 border border-emerald-500/15">
        <Receipt size={28} className="text-emerald-400/80" strokeWidth={1.6} />
      </div>
      <p className="text-white/85 font-bold text-base">No transactions found</p>
      <p className="text-white/50 text-[13px] mt-1.5">
        {hasFilter ? "Try changing the filter or date range." : "Your transactions will appear here once you start using your wallet."}
      </p>
    </div>
  );
}
